import {AlertCircle, Check, Clock, ExternalLink, Loader2} from "lucide-react"
import React, {useMemo, useState} from "react"

import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card"
import {HoverCard, HoverCardContent, HoverCardTrigger} from "@/components/ui/hover-card"
import {Tabs, TabsList, TabsTrigger} from "@/components/ui/tabs"

interface PlatformWeaviateArticle {
    documentId: string
    name: string
    site: string
    url: string
    type: string
    synopsis: string
    strapline: string
    authors: string[]
    createdDate: string
    modifiedDate: string
    contentModifiedDate: string
    publishedDate: string
    heroImage?: string
    listingHeroImage?: string
}

interface Article {
    article: PlatformWeaviateArticle
    articleHitsData: Record<string, number>
}

// First, update the interfaces to match the new data structure
interface HitsReport {
    confidence_level_rows: ConfidenceLevelRow[]
    report_id: string
    segment_name: string
    article_count: number
    status: Status
    error?: string
    update_count: number
}

interface ConfidenceLevelRow {
    confidence_level: number
    cumulative_article_count: number
    cumulative_article_hits: Record<string, number>
    least_relevant_articles: Article[]
}

type Status = "Initialising" | "Loading" | "Complete" | "Error"

interface ArticleHitsReportMessageProps {
    message: string
}

const isInvalidDate = (dateString: string) => {
    // Check for specific invalid date strings
    if (dateString === "0001-01-01T00:00:00Z" || dateString === "1970-01-01T00:00:00Z") {
        return true
    }

    // Parse the date and check if it's valid
    const date = new Date(dateString)
    return isNaN(date.getTime())
}

const formatDate = (dateString: string) => {
    if (isInvalidDate(dateString)) return "n/a"

    const date = new Date(dateString)

    const day = date.getDate().toString().padStart(2, "0")
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const year = date.getFullYear()
    const hours = date.getHours().toString().padStart(2, "0")
    const minutes = date.getMinutes().toString().padStart(2, "0")

    return `${year}/${month}/${day} ${hours}:${minutes}`
}

const DateDisplay: React.FC<{label: string; date: string}> = ({label, date}) => {
    const formattedDate = formatDate(date)

    return (
        <div>
            <div className="font-medium">{label}</div>
            <div className="text-gray-500">{formattedDate}</div>
        </div>
    )
}
const HitsBreakdown: React.FC<{hits: Record<string, number>}> = ({hits = {}}) => {
    const sortedEntries = Object.entries(hits).sort((a, b) => b[1] - a[1])

    return (
        <div className="grid gap-2">
            {sortedEntries.map(([country, count]) => (
                <div key={country} className="flex justify-between items-center text-sm">
                    <span className="font-medium">{country}</span>
                    <span className="text-gray-600">{count.toLocaleString()}</span>
                </div>
            ))}
        </div>
    )
}

type MetricsSectionProps = {
    articleCount?: number
    hits?: Record<string, number>
}

const MetricsSection = React.memo<MetricsSectionProps>(({articleCount, hits}) => {
    const safeHits = hits ?? {}
    const totalHits = Object.values(safeHits).reduce((sum, count) => sum + count, 0)
    const safeArticleCount = articleCount ?? 0

    return (
        <div className="grid grid-cols-3 gap-6">
            <div>
                <h3 className="text-lg font-medium mb-3">Article Count</h3>
                <div className="text-2xl font-semibold">{safeArticleCount.toLocaleString()}</div>
            </div>
            <div>
                <h3 className="text-lg font-medium mb-3">Total Article Hits</h3>
                <div className="text-2xl font-semibold">{totalHits.toLocaleString()}</div>
            </div>
            <div>
                <h3 className="text-lg font-medium mb-3">Article Hits by Country</h3>
                <HitsBreakdown hits={safeHits} />
            </div>
        </div>
    )
})

const CompactHitsBreakdown: React.FC<{hits: Record<string, number>}> = ({hits = {}}) => {
    const totalHits = Object.values(hits).reduce((sum, count) => sum + count, 0)
    const sortedEntries = Object.entries(hits).sort((a, b) => b[1] - a[1])

    return (
        <div className="flex items-center gap-3 text-sm">
            <div className="font-medium">{totalHits.toLocaleString()} hits</div>
            <div className="text-gray-500">
                (
                {sortedEntries.map(([country, count], index) => (
                    <span key={country}>
                        {country}: {count.toLocaleString()}
                        {index < sortedEntries.length - 1 ? ", " : ""}
                    </span>
                ))}
                )
            </div>
        </div>
    )
}

const ArticleCard = React.memo<{article: Article}>(({article}) => {
    const {article: details, articleHitsData} = article

    const thumbnailImage = details.heroImage || details.listingHeroImage

    return (
        <HoverCard>
            <HoverCardTrigger asChild>
                <div className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <div className="flex items-start gap-4">
                        {thumbnailImage && (
                            <div className="flex-shrink-0">
                                <img src={thumbnailImage} alt={details.name} className="w-16 h-16 object-cover rounded" />
                            </div>
                        )}
                        <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                    <h4 className="font-medium">{details.name}</h4>
                                    <div className="text-sm text-gray-500 mt-1">
                                        <span className="mr-2">{details.site}</span>
                                        <span className="mr-2">•</span>
                                        <span>{details.type}</span>
                                    </div>
                                </div>
                                <a
                                    href={details.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:text-blue-600"
                                >
                                    <ExternalLink size={16} />
                                </a>
                            </div>
                            <CompactHitsBreakdown hits={articleHitsData} />
                        </div>
                    </div>
                </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-96">
                <div className="space-y-2">
                    {details.synopsis && (
                        <div>
                            <div className="font-medium">Synopsis</div>
                            <div className="text-sm text-gray-500">{details.synopsis}</div>
                        </div>
                    )}
                    {details.strapline && (
                        <div>
                            <div className="font-medium">Strapline</div>
                            <div className="text-sm text-gray-500">{details.strapline}</div>
                        </div>
                    )}
                    {details.authors?.length > 0 && (
                        <div>
                            <div className="font-medium">Authors</div>
                            <div className="text-sm text-gray-500">{details.authors.join(", ")}</div>
                        </div>
                    )}
                    <div>
                        <div className="font-medium">ID</div>
                        <div className="text-gray-500">{details.documentId}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <DateDisplay label="Created" date={details.createdDate} />
                        <DateDisplay label="Modified" date={details.modifiedDate} />
                        <DateDisplay label="Content Modified" date={details.contentModifiedDate} />
                        <DateDisplay label="Published" date={details.publishedDate} />
                    </div>
                </div>
            </HoverCardContent>
        </HoverCard>
    )
})

// New status display component
const StatusDisplay: React.FC<{status: Status}> = ({status}) => {
    const statusConfig = {
        Initialising: {icon: Clock, color: "text-orange-500", text: "Initialising"},
        Loading: {icon: Loader2, color: "text-blue-500 animate-spin", text: "Loading"},
        Complete: {icon: Check, color: "text-green-500", text: "Complete!"},
        Error: {icon: AlertCircle, color: "text-red-500", text: "Error"},
    }

    const config = statusConfig[status]
    const Icon = config.icon

    return (
        <div className="flex items-center justify-end gap-2">
            <Icon className={`h-4 w-4 ${config.color}`} />
            <span>{config.text}</span>
        </div>
    )
}

const ArticleHitsReportMessage: React.FC<ArticleHitsReportMessageProps> = ({message}) => {
    // Parse the report
    const report = useMemo(() => {
        try {
            return JSON.parse(message) as HitsReport
        } catch (error) {
            console.error("Failed to parse report:", error)
            return null
        }
    }, [message])

    if (!report?.confidence_level_rows?.length) return <div>No data available</div>

    const {reportId, segmentName, confidenceLevels} = useMemo(
        () => ({
            reportId: report.report_id,
            segmentName: report.segment_name,
            confidenceLevels: Array.from(new Set(report.confidence_level_rows.map((row) => row.confidence_level))).sort(),
        }),
        [report],
    )

    const [selectedConfidence, setSelectedConfidence] = useState(confidenceLevels[0])

    const currentRow = useMemo(
        () => report.confidence_level_rows.find((row) => row.confidence_level === selectedConfidence) ?? report.confidence_level_rows[0],
        [report, selectedConfidence],
    )

    return (
        <Card className="w-full max-w-6xl mx-auto">
            <CardHeader>
                <div className="flex justify-between items-start">
                    <div>
                        <CardTitle>Segment Report</CardTitle>
                        <div className="text-sm text-gray-500">
                            <div>Report ID: {reportId}</div>
                            <div>Segment Name: {segmentName}</div>
                        </div>
                    </div>
                    <div className="text-right space-y-1">
                        <StatusDisplay status={report.status} />
                        <div className="text-sm text-gray-500">Processed {report.article_count?.toLocaleString() || 0} articles</div>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {report.error ? (
                    <div className="text-red-500">{report.error}</div>
                ) : (
                    <div className="space-y-4">
                        <div className="relative w-full overflow-hidden">
                            <div className="overflow-x-auto pb-3">
                                <Tabs
                                    value={selectedConfidence.toString()}
                                    onValueChange={(val) => setSelectedConfidence(parseFloat(val))}
                                    className="w-full"
                                >
                                    <TabsList className="inline-flex h-10 items-center justify-start w-auto">
                                        {confidenceLevels.map((level) => (
                                            <TabsTrigger key={level} value={level.toString()} className="whitespace-nowrap">
                                                {level.toFixed(3)}
                                            </TabsTrigger>
                                        ))}
                                    </TabsList>
                                </Tabs>
                            </div>
                        </div>

                        <div className="border rounded-lg p-4">
                            <div className="space-y-6">
                                <MetricsSection
                                    articleCount={currentRow.cumulative_article_count}
                                    hits={currentRow.cumulative_article_hits}
                                />
                                <div>
                                    <h3 className="text-lg font-medium mb-3">Least Relevant Articles</h3>
                                    <div className="space-y-2 max-h-96 overflow-y-auto pr-2 border rounded-lg">
                                        {currentRow.least_relevant_articles?.map((article) => (
                                            <ArticleCard key={article.article.documentId} article={article} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export default ArticleHitsReportMessage
